import React, { CSSProperties, FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Cart.module.scss';

interface IProps {
    value: string;
    options: string[];
    onChange: (value: string) => void;
}

export const SelectInput: FC<IProps> = ({ value, options, onChange }) => {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const openAbove = window.innerHeight - rect.bottom < 168 && rect.top > 168;
        setMenuStyle(
            openAbove
                ? { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width }
                : { top: rect.bottom + 8, left: rect.left, width: rect.width }
        );
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const onPointer = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        const onScroll = () => setOpen(false);

        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);
        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [open]);

    const choose = (next: string) => {
        onChange(next);
        setOpen(false);
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={styles.selectTrigger}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
            >
                <span className={value ? styles.selectValue : styles.selectPlaceholder}>
                    {value || '—'}
                </span>
                <span className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`} />
            </button>
            {open &&
                createPortal(
                    <ul
                        ref={menuRef}
                        className={styles.selectMenu}
                        role="listbox"
                        style={menuStyle}
                    >
                        <li role="presentation">
                            <button
                                type="button"
                                role="option"
                                aria-selected={value === ''}
                                className={value === '' ? styles.selectOptionActive : styles.selectOption}
                                onClick={() => choose('')}
                            >
                                —
                            </button>
                        </li>
                        {options.map((option) => {
                            const active = option === value;
                            return (
                                <li key={option} role="presentation">
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={active}
                                        className={active ? styles.selectOptionActive : styles.selectOption}
                                        onClick={() => choose(option)}
                                    >
                                        {option}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>,
                    document.body
                )}
        </>
    );
};
